import { useState } from "react";

import { Box, Grid, Stack } from "@mui/material";

import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";
import CustomSelection from "../../../../helper-components/form/CustomSelection";
import CustomButton from "../../../../helper-components/input/CustomButton";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";

import PatientAddStepper from "../../patients/components/PatientAddStepper";

import { createUploadObject, getEllipsis } from "../../../../helper-functions/shared/miscUtils";
import { getPatientDisplayName, normalizePatientRowForUpload } from "../../../../helper-functions/shared/patientDisplay";
import {
    createPatient,
    getDuplicatePatientBlockedMessage,
    getExistingPatientReuseMessage,
    matchPatients,
    querySelectPatients
} from "../../../../helper-functions/api/patientQueries";
import { PATIENT_NULLABLE_FIELDS } from "../../../../config/constants/Constants";

export default function PatientData(props) {
    const { state, setState, handleNext, practicePatient = null } = props;
    const isPractice = Boolean(practicePatient);

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new patient",
            component: <PatientAddStepper {...props} callback={(confirm, options) => handlePatientCreate(confirm, options)} />
        });
    };

    const showSelectDialog = () => {
        // Local state for dialog selection - separate from main state until confirmed.
        function SelectDialogComponent() {
            const [localState, setLocalState] = useState(state || null);

            const handleSelection = (selected = []) => {
                setLocalState(selected?.[0] || null);
            };

            const handleCancelClick = () => {
                props.setOpenDialog(false);
            };

            const handleNextClick = () => {
                setState(localState);
                props.setOpenDialog(false);

                handleNext();
            };

            return (
                <Box className="box">
                    <CustomSelection
                        {...props}
                        selected={localState ? [localState] : []}
                        callback={handleSelection}
                        query={
                            isPractice
                                ? (_props, store) => {
                                      store.setData([practicePatient]);
                                  }
                                : querySelectPatients
                        }
                        getPrimarySearchText={(el) => [el?.patient_identifier, el?.first_name, el?.last_name].filter(Boolean).join(" ")}
                        getSecondarySearchText={(el) => `${el?.notes || ""} #${el?.id ?? ""}`.trim()}
                        getPrimaryText={(el) => getPatientDisplayName(el)}
                        getSecondaryText={(el) => getEllipsis(el?.notes, 50)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
                        <CustomButton {...props} title={"Cancel"} onClick={handleCancelClick} className="button" fullWidth variant="outlined" />
                        <CustomButton {...props} title={"Next"} onClick={handleNextClick} className="button" fullWidth disabled={localState === null} />
                    </Stack>
                </Box>
            );
        }

        props.showDialog({
            title: isPractice ? "Select practice patient" : "Select patient",
            component: <SelectDialogComponent />
        });
    };

    const handlePatientCreate = async (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const cleanRow = normalizePatientRowForUpload(row);
        const matches = await matchPatients(props, cleanRow);

        if (matches.length === 1) {
            props.showDialog({
                title: "Existing patient found",
                message: getExistingPatientReuseMessage(matches[0]),
                actionTitle: "Use existing patient",
                callback: (reuse) => {
                    if (reuse) {
                        setState(matches[0]);
                        handleNext();
                        return;
                    }

                    props.showSnackbar({
                        color: "error",
                        message: getDuplicatePatientBlockedMessage(cleanRow)
                    });
                }
            });
            return;
        }

        // Send request to API.
        const body = createUploadObject(cleanRow, ["form", "_*"], ["*"], PATIENT_NULLABLE_FIELDS);
        createPatient(props, { body }, handleCreateCallback);
    };

    const handleCreateCallback = (response) => {
        const newPatient = response.body;
        setState(newPatient);

        handleNext();
    };

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <TransparentFragment
                    value={
                        state
                            ? `Patient: ${getPatientDisplayName(state)}`
                            : isPractice
                              ? "Select the practice patient from the list. There is one patient, the same step as a real examination."
                              : "Select a patient from the list below."
                    }
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
                    {!isPractice && (
                        <CustomButton
                            {...props}
                            title="Add new patient"
                            onClick={showCreateDialog}
                            variant="outlined"
                            fullWidth
                            startIcon={<PersonAddIcon />}
                            className="button-large-v2"
                        />
                    )}
                    <CustomButton
                        {...props}
                        title="Select existing patient"
                        onClick={showSelectDialog}
                        variant="outlined"
                        fullWidth
                        startIcon={<SearchIcon />}
                        className="button-large-v2"
                    />
                </Stack>
            </Grid>
        </Grid>
    );
}
